import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const SUBJECTS = [
  { key: "general", label: "Question générale" },
  { key: "support", label: "Support technique" },
  { key: "partner", label: "Partenariat" },
  { key: "billing", label: "Facturation / commande" },
  { key: "bug", label: "Signaler un bug" },
  { key: "suggestion", label: "Suggestion" },
  { key: "press", label: "Presse" },
  { key: "other", label: "Autre" },
];

export default function Contact() {
  const [state, setState] = useState({ name: "", email: "", subject_key: "general", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (state.message.trim().length < 5) return toast.error("Votre message est trop court.");
    setSending(true);
    try {
      await api.post("/contact", {
        name: state.name.trim(),
        email: state.email.trim(),
        subject: SUBJECTS.find((s) => s.key === state.subject_key)?.label || "Message via le site web",
        subject_key: state.subject_key,
        message: state.message.trim(),
        source: "app_web",
      });
      toast.success("Message envoyé — nous vous répondrons sous 24h.");
      setState({ name: "", email: "", subject_key: "general", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Envoi impossible pour le moment. Réessayez plus tard.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-10 py-16 lg:py-20" data-testid="contact-page">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="eyebrow">Contact</div>
          <h1 className="mt-3 font-display text-4xl lg:text-5xl font-black tracking-tighter leading-[1.05]">
            On reste à<br />votre écoute.
          </h1>
          <p className="mt-4 text-muted-foreground max-w-md">
            Une question technique, un retour d'expérience, un besoin sur mesure ?
            Écrivez-nous, on répond en moins de 24h.
          </p>

          <div className="mt-10 space-y-6">
            <div className="flex items-start gap-4">
              <span className="h-10 w-10 rounded-lg bg-primary/10 text-primary grid place-items-center">
                <Mail className="h-5 w-5" />
              </span>
              <div>
                <div className="eyebrow">Email</div>
                <div className="mt-1 font-medium">contact@doctospa.fr</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <span className="h-10 w-10 rounded-lg bg-primary/10 text-primary grid place-items-center">
                <Clock className="h-5 w-5" />
              </span>
              <div>
                <div className="eyebrow">Réponse moyenne</div>
                <div className="mt-1 font-medium">&lt; 24h ouvrées</div>
              </div>
            </div>
          </div>
        </div>

        <Card className="lg:col-span-7 p-8 lg:p-10 border-black/10">
          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name">Nom</Label>
              <Input id="name" required value={state.name}
                onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
                data-testid="contact-name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={state.email}
                onChange={(e) => setState((s) => ({ ...s, email: e.target.value }))}
                data-testid="contact-email" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="subject">Sujet</Label>
              <select
                id="subject"
                value={state.subject_key}
                onChange={(e) => setState((s) => ({ ...s, subject_key: e.target.value }))}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="contact-subject"
              >
                {SUBJECTS.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <Textarea id="message" rows={6} required value={state.message}
                onChange={(e) => setState((s) => ({ ...s, message: e.target.value }))}
                data-testid="contact-message" />
            </div>
            <Button type="submit" className="rounded-full h-11 px-8" disabled={sending} data-testid="contact-submit">
              {sending ? "Envoi..." : "Envoyer le message"}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
